import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { userAPI, adminAPI, authAPI, activityAPI } from '../services/api';
import { getActivitySessionId } from '../utils/activitySession';

// Auth Context
const AuthContext = createContext();

// Auth Actions
const AUTH_ACTIONS = {
  LOGIN: 'LOGIN',
  LOGOUT: 'LOGOUT',
  REGISTER: 'REGISTER',
  UPDATE_PROFILE: 'UPDATE_PROFILE',
  LOAD_USER: 'LOAD_USER',
  UPDATE_EMAIL_VERIFIED: 'UPDATE_EMAIL_VERIFIED'
};

// Auth Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN:
    case AUTH_ACTIONS.REGISTER:
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload,
        loading: false
      };

    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        loading: false
      };

    case AUTH_ACTIONS.UPDATE_PROFILE:
      return {
        ...state,
        user: { ...state.user, ...action.payload }
      };

    case AUTH_ACTIONS.UPDATE_EMAIL_VERIFIED:
      return {
        ...state,
        user: { ...state.user, emailVerified: action.payload }
      };

    case AUTH_ACTIONS.LOAD_USER:
      return {
        ...state,
        isAuthenticated: action.payload ? true : false,
        user: action.payload,
        loading: false
      };

    default:
      return state;
  }
};

// Initial State
const initialState = {
  isAuthenticated: false,
  user: null,
  loading: true
};

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Load user from localStorage on mount
  useEffect(() => {
    const loadUser = async () => {
      // Small delay to ensure loading state is visible
      await new Promise(resolve => setTimeout(resolve, 100));
      
      try {
        const savedUser = localStorage.getItem('anadolu-user');
        const authToken = localStorage.getItem('authToken');
        
        // Token yoksa user bilgisini de temizle
        if (!authToken) {
          localStorage.removeItem('anadolu-user');
          dispatch({ type: AUTH_ACTIONS.LOAD_USER, payload: null });
          return;
        }
        
        if (savedUser) {
          try {
            const userData = JSON.parse(savedUser);
            
            // Admin oturumu yalnızca gerçek bir token varsa sürdürülür
            if (userData.role === 'admin' && !authToken) {
              // Token eşleşmiyorsa temizle
              localStorage.removeItem('anadolu-user');
              localStorage.removeItem('authToken');
              dispatch({ type: AUTH_ACTIONS.LOAD_USER, payload: null });
              return;
            }
            
            // User verisi ve token geçerli - user'ı yükle
            dispatch({ type: AUTH_ACTIONS.LOAD_USER, payload: userData });
          } catch (error) {
            console.error('Kullanıcı verisi yüklenirken hata:', error);
            localStorage.removeItem('anadolu-user');
            localStorage.removeItem('authToken');
            dispatch({ type: AUTH_ACTIONS.LOAD_USER, payload: null });
          }
        } else {
          // User bilgisi yoksa token'ı da temizle
          if (authToken) {
            localStorage.removeItem('authToken');
          }
          dispatch({ type: AUTH_ACTIONS.LOAD_USER, payload: null });
        }
      } catch (error) {
        console.error('User yükleme hatası:', error);
        dispatch({ type: AUTH_ACTIONS.LOAD_USER, payload: null });
      }
    };
    
    loadUser();
  }, []);

  // Save user to localStorage whenever user changes
  useEffect(() => {
    // İlk yükleme (loadUser) henüz tamamlanmadan bu efekt tetiklenirse
    // state.user başlangıç değeri olan null'dır ve aşağıdaki else dalı
    // localStorage'daki gerçek oturumu loadUser okumadan silerdi.
    if (state.loading) return;
    if (state.user) {
      localStorage.setItem('anadolu-user', JSON.stringify(state.user));
      // Token uydurulmaz: gerçek token yalnızca giriş uçlarından gelir.
      // Token yoksa oturum geçersizdir ve temizlenir.
      if (!localStorage.getItem('authToken')) {
        localStorage.removeItem('anadolu-user');
      }
    } else {
      localStorage.removeItem('anadolu-user');
      localStorage.removeItem('authToken');
    }
  }, [state.user, state.loading]);

  // Auth Actions
  const loginAsAdmin = (adminFromApi, token) => {
    const admin = adminFromApi?.data ?? adminFromApi;
    const adminUser = {
      id: admin.id,
      firstName: admin.firstName ?? 'Admin',
      lastName: admin.lastName ?? '',
      email: admin.email,
      role: 'admin',
      // Panel içi yetki ayrımı: ADMIN | SUPER_ADMIN (asıl kontrol backend'de)
      adminRole: admin.role || 'ADMIN',
      isActive: admin.isActive !== false,
      createdAt: admin.createdAt || new Date().toISOString(),
      joinDate: admin.createdAt || new Date().toISOString(),
      orders: [],
      favorites: []
    };
    localStorage.setItem('authToken', token);
    dispatch({ type: AUTH_ACTIONS.LOGIN, payload: adminUser });
    return { success: true, message: 'Admin girişi başarılı!' };
  };

  const login = async (email, password) => {
    try {
      // Gerçek JWT üreten endpoint (backend returns { success, message, data: { token, user } })
      const response = await authAPI.login(email, password);
      const result = response.data?.data ?? response.data;
      const payload = result.user;
      const user = {
        ...payload,
        role: 'customer',
        joinDate: payload.createdAt,
        orders: [],
        favorites: []
      };

      localStorage.setItem('authToken', result.token);
      dispatch({ type: AUTH_ACTIONS.LOGIN, payload: user });
      // Anonim oturumda biriken tercih geçmişini hesaba taşı
      activityAPI.merge(getActivitySessionId()).catch(() => {});
      return { success: true, message: 'Giriş başarılı!' };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.response?.data?.error || 'Giriş başarısız!'
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await userAPI.register(userData);
      const payload = response.data?.data ?? response.data;
      const newUser = {
        ...payload,
        role: 'customer',
        joinDate: payload.createdAt,
        orders: [],
        favorites: []
      };

      // Kayıt endpoint'i JWT döndürmüyor; hemen ardından gerçek token almak için giriş yapılır.
      const loginResponse = await authAPI.login(userData.email, userData.password);
      const loginResult = loginResponse.data?.data ?? loginResponse.data;
      localStorage.setItem('authToken', loginResult.token);

      dispatch({ type: AUTH_ACTIONS.REGISTER, payload: newUser });
      activityAPI.merge(getActivitySessionId()).catch(() => {});
      return { success: true, message: 'Kayıt başarılı!' };
    } catch (error) {
      console.error('Register error:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.response?.data?.error || 'Kayıt başarısız!'
      };
    }
  };

  // Google OAuth callback sayfasından çağrılır: backend zaten JWT üretti, sadece
  // kullanıcı profilini çekip session'ı kurar.
  const loginWithOAuthToken = async (token) => {
    try {
      localStorage.setItem('authToken', token);
      const response = await authAPI.validate(token);
      const result = response.data?.data ?? response.data;
      const payload = result.user;
      const user = {
        ...payload,
        role: 'customer',
        joinDate: payload.createdAt,
        orders: [],
        favorites: []
      };

      dispatch({ type: AUTH_ACTIONS.LOGIN, payload: user });
      activityAPI.merge(getActivitySessionId()).catch(() => {});
      return { success: true };
    } catch (error) {
      console.error('OAuth login error:', error);
      localStorage.removeItem('authToken');
      return { success: false, message: 'Google ile giriş başarısız oldu.' };
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    dispatch({ type: AUTH_ACTIONS.LOGOUT });
  };

  const updateProfile = async (profileData) => {
    try {
      if (!state.user || !state.user.id) {
        throw new Error('Kullanıcı bilgisi bulunamadı');
      }

      // API'ye profil güncelleme isteği gönder (backend returns { success, message, data: user })
      const response = await userAPI.updateUser(state.user.id, profileData);
      const payload = response.data?.data ?? response.data;

      // Başarılı olursa local state'i güncelle
      dispatch({ type: AUTH_ACTIONS.UPDATE_PROFILE, payload });

      return { success: true, data: payload };
    } catch (error) {
      console.error('Profil güncellenirken hata:', error);
      const msg = error.response?.data?.message || error.response?.data?.error || error.message;
      return { success: false, error: msg || 'Profil güncellenirken bir hata oluştu' };
    }
  };

  const addToFavorites = (productId) => {
    if (state.user && !state.user.favorites.includes(productId)) {
      const updatedFavorites = [...state.user.favorites, productId];
      updateProfile({ favorites: updatedFavorites });
    }
  };

  const removeFromFavorites = (productId) => {
    if (state.user) {
      const updatedFavorites = state.user.favorites.filter(id => id !== productId);
      updateProfile({ favorites: updatedFavorites });
    }
  };

  const isFavorite = (productId) => {
    return state.user ? state.user.favorites.includes(productId) : false;
  };

  const updateEmailVerified = (verified) => {
    dispatch({
      type: AUTH_ACTIONS.UPDATE_EMAIL_VERIFIED,
      payload: verified
    });
  };

  const value = {
    isAuthenticated: state.isAuthenticated,
    user: state.user,
    loading: state.loading,
    login,
    loginAsAdmin,
    loginWithOAuthToken,
    register,
    logout,
    updateProfile,
    addToFavorites,
    removeFromFavorites,
    isFavorite,
    updateEmailVerified
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom Hook to use Auth Context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth hook AuthProvider içinde kullanılmalıdır');
  }
  return context;
};

export default AuthContext;
